import { useEffect, useMemo, useState } from "react";
import { subDays } from "date-fns";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { usePostAggregatePizzas } from "@/api/pizza-checker/pizzas/pizzas";
import { useDashboardDateRange } from "@/context/dashboard-date-range-context";
import { useBranchFilter } from "@/hooks/use-branch-filter";
import { cn } from "@/lib/utils";
import { useLocalization } from "@/context/localization-context";

type CaptureQualityCounts = {
  totalCaptures: number;
  noPizza: number;
  evaluableImages: number;
};

type CaptureQualityRow = {
  key: "no-pizza" | "obstacle" | "evaluable";
  label: string;
  count: number;
  percentage: string;
};

const EMPTY_COUNTS: CaptureQualityCounts = {
  totalCaptures: 0,
  noPizza: 0,
  evaluableImages: 0,
};

const readCount = (value: unknown) =>
  typeof value === "number" && Number.isFinite(value) ? value : 0;

const getIndicatorClass = (key: CaptureQualityRow["key"]) => {
  switch (key) {
    case "no-pizza":
      return "bg-blue-500";
    case "obstacle":
      return "bg-amber-500";
    case "evaluable":
      return "bg-green-500";
  }
};

export function ImageCaptureQualityCard({
  className,
}: {
  className?: string;
}) {
  const [counts, setCounts] = useState<CaptureQualityCounts>(EMPTY_COUNTS);
  const { processedDateRange } = useDashboardDateRange();
  const branchFilter = useBranchFilter();
  const { mutateAsync, isPending } = usePostAggregatePizzas({});
  const { t, tp, formatNumber } = useLocalization();

  const formatPercentage = (count: number, total: number) => {
    if (total <= 0) {
      return formatNumber(0, { style: "percent" });
    }

    return formatNumber(count / total, {
      style: "percent",
      minimumFractionDigits: 1,
      maximumFractionDigits: 1,
    });
  };

  const { start, end } = useMemo(() => {
    if (processedDateRange.start && processedDateRange.end) {
      return { start: processedDateRange.start, end: processedDateRange.end };
    }

    const endDate = new Date();
    return { start: subDays(endDate, 7), end: endDate };
  }, [processedDateRange]);

  useEffect(() => {
    let isMounted = true;

    async function fetchCaptureQualityStats() {
      const pipeline = [
        {
          $match: {
            capture_at: {
              $gte: start.toISOString(),
              $lte: end.toISOString(),
            },
            ...branchFilter,
          },
        },
        {
          $group: {
            _id: null,
            totalCaptures: { $sum: 1 },
            noPizza: {
              $sum: {
                $cond: [
                  { $eq: ["$evaluation.metadata.classification", "No Pizza"] },
                  1,
                  0,
                ],
              },
            },
            evaluableImages: {
              $sum: {
                $cond: [
                  {
                    $and: [
                      {
                        $ne: [
                          "$evaluation.metadata.classification",
                          "No Pizza",
                        ],
                      },
                      {
                        $ne: [
                          {
                            $type: "$evaluation.metadata.classification",
                          },
                          "missing",
                        ],
                      },
                      { $ne: ["$evaluation.metadata.classification", null] },
                      {
                        $or: [
                          {
                            $eq: [
                              "$evaluation.metadata.photo_quality_evaluation.status",
                              "OK",
                            ],
                          },
                          {
                            $eq: [
                              {
                                $type:
                                  "$evaluation.metadata.photo_quality_evaluation.status",
                              },
                              "missing",
                            ],
                          },
                          {
                            $eq: [
                              "$evaluation.metadata.photo_quality_evaluation.status",
                              null,
                            ],
                          },
                        ],
                      },
                    ],
                  },
                  1,
                  0,
                ],
              },
            },
          },
        },
      ];

      try {
        const response = await mutateAsync({ data: { pipeline } });

        if (!isMounted) {
          return;
        }

        const result = response.results[0] ?? {};
        setCounts({
          totalCaptures: readCount(result.totalCaptures),
          noPizza: readCount(result.noPizza),
          evaluableImages: readCount(result.evaluableImages),
        });
      } catch (error) {
        console.error("Failed to fetch image capture quality stats", error);
        if (isMounted) {
          setCounts(EMPTY_COUNTS);
        }
      }
    }

    fetchCaptureQualityStats();

    return () => {
      isMounted = false;
    };
  }, [branchFilter, end, mutateAsync, start]);

  const rows = useMemo<CaptureQualityRow[]>(() => {
    const obstacleCount = Math.max(
      counts.totalCaptures - counts.noPizza - counts.evaluableImages,
      0,
    );

    return [
      {
        key: "no-pizza",
        label: t("captureQuality.noPizza"),
        count: counts.noPizza,
        percentage: formatPercentage(counts.noPizza, counts.totalCaptures),
      },
      {
        key: "obstacle",
        label: t("captureQuality.obstacle"),
        count: obstacleCount,
        percentage: formatPercentage(obstacleCount, counts.totalCaptures),
      },
      {
        key: "evaluable",
        label: t("captureQuality.evaluable"),
        count: counts.evaluableImages,
        percentage: formatPercentage(
          counts.evaluableImages,
          counts.totalCaptures,
        ),
      },
    ];
  }, [counts, formatNumber, t]);

  return (
    <Card className={cn("@container/card bg-card border shadow-sm", className)}>
      <CardHeader className="pb-3">
        <CardTitle className="text-base font-semibold">
          {t("captureQuality.title")}
        </CardTitle>
        <CardDescription>
          {isPending
            ? t("captureQuality.loading")
            : tp("captureQuality.periodPhotos", counts.totalCaptures, {
                count: formatNumber(counts.totalCaptures),
              })}
        </CardDescription>
      </CardHeader>
      <CardContent>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{t("captureQuality.category")}</TableHead>
              <TableHead className="text-right">
                {t("captureQuality.count")}
              </TableHead>
              <TableHead className="text-right">
                {t("captureQuality.share")}
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((row) => (
              <TableRow key={row.key}>
                <TableCell className="font-medium">
                  <div className="flex items-center gap-2">
                    <span
                      className={cn(
                        "h-2.5 w-2.5 rounded-full",
                        getIndicatorClass(row.key),
                      )}
                    />
                    <span>{row.label}</span>
                  </div>
                </TableCell>
                <TableCell className="text-right tabular-nums">
                  {formatNumber(row.count)}
                </TableCell>
                <TableCell className="text-right tabular-nums">
                  {row.percentage}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
}
